import { Box, Stack, Typography } from '@mui/material';
import { isEmpty } from 'lodash-es';
import { memo } from 'react';
import { Outcome, PublishedEventParticipants, SpecialValue } from '../../@types';
import { getOutcomeName } from '../../helpers';
import { BetDetails } from './BetDetails';
import useLocalization from '../../hooks/useLocalization';
import { MarketDisplayType } from '../../types/enums';
import { useSkinStyles } from '../../hooks/useSkinStyles';
import {
  getOutcomeTableCellSkinStyles,
  type OutcomeTableCellSkinStyles,
} from '../../styles/components/outcomeTableCell';
import { useIsOutcomeSelected } from '../../stores/betslip/selectors';
import { publishBetslipAnimationOrigin } from '../../utils/betslipAnimationOrigin';
import { mergeSx } from '../../utils/styles';
import OutcomeTableCellOdds from './OutcomeTableCellOdds';

type OutcomeTableCellProps = {
  outcome?: Outcome | null; // optional because we want to render empty cell if there is no outcome
  disabled?: boolean;
  specialValues?: SpecialValue[];
  onClick?: () => void;
  hideOutcomeName?: boolean;
  isMarketSingleDisplayType: boolean;
  marketTypeName?: string;
  marketName?: string;
  marketDisplayType?: MarketDisplayType;
  participants?: PublishedEventParticipants;
  eventName?: string;
  sportId: string;
  mixedMarket?: boolean;
  columnStyle?: boolean;
  alignCenter?: boolean;
  numericOnly?: boolean;
  searchVariant?: boolean;
  featuredCardVariant?: boolean;
};

const getResponsiveCellDirection = (columnStyle: boolean, searchVariant: boolean) =>
  ({
    xs: columnStyle ? 'column' : 'row',
    md: searchVariant ? 'column' : 'row',
  }) as const;

const getOutcomeTypographyProps = (mixedMarket: boolean, shouldWrapText: boolean) => {
  if (!mixedMarket) {
    return {};
  }

  return {
    noWrap: !shouldWrapText,
    maxWidth: '100%',
  };
};

const getVisibleOutcomeName = (
  outcome: Outcome | null | undefined,
  hideOutcomeName: boolean,
  hasOMSpecialValues: boolean,
  isMarketSingleDisplayType: boolean
) => {
  if (!outcome) {
    return null;
  }

  if (hideOutcomeName || hasOMSpecialValues) {
    return null;
  }

  return getOutcomeName(outcome, isMarketSingleDisplayType);
};

// TODO: Refactor/simplify after OM switch (keep as legacy component?)
const OutcomeTableCell = ({
  outcome,
  specialValues,
  onClick,
  hideOutcomeName = false, // used to hide outcome name and special values on In play page
  disabled,
  isMarketSingleDisplayType,
  marketTypeName,
  marketName,
  marketDisplayType,
  participants,
  eventName,
  sportId,
  mixedMarket = false,
  columnStyle = false,
  alignCenter = false,
  numericOnly = false,
  searchVariant = false,
  featuredCardVariant = false,
}: OutcomeTableCellProps) => {
  const { getLocalizedOutcomeName } = useLocalization();

  const hasEmptyOdds = outcome?.odds === '1.00' && outcome.isAvailable;
  const isEmptyCell = !outcome || hasEmptyOdds;

  const selected = useIsOutcomeSelected(outcome && !isEmptyCell ? outcome.id : undefined);
  const hasOMSpecialValues = !!outcome && !isEmpty(specialValues);
  const hasFTBSpecialValue = !!outcome?.properties?.specialValue;

  const isUnavailableOutcome = !outcome?.isAvailable;
  const interactionDisabled = disabled || isEmptyCell || isUnavailableOutcome;

  const styles = useSkinStyles<OutcomeTableCellSkinStyles>(
    getOutcomeTableCellSkinStyles(selected, interactionDisabled, searchVariant, mixedMarket, featuredCardVariant)
  );

  const outcomeName = getVisibleOutcomeName(outcome, hideOutcomeName, hasOMSpecialValues, isMarketSingleDisplayType);

  const shouldCenterBecauseOfValue = hasOMSpecialValues || hasFTBSpecialValue || hideOutcomeName;
  const shouldCenterBecauseOfLayout = mixedMarket || alignCenter;
  const displayCentered = shouldCenterBecauseOfValue || shouldCenterBecauseOfLayout;
  const shouldShowFTBSpecialValue = !hasOMSpecialValues && hasFTBSpecialValue && hideOutcomeName;

  const handleOverUnder = (outcomeName: string) => {
    let outcomeNameKey = '';
    if (outcomeName.includes('Over')) {
      outcomeNameKey = 'Over';
    } else if (outcomeName.includes('Under')) {
      outcomeNameKey = 'Under';
    }
    return ` ${getLocalizedOutcomeName(outcomeNameKey)}`;
  };

  const cellDirection = getResponsiveCellDirection(columnStyle, searchVariant);
  const cellAlignment = displayCentered ? 'center' : 'flex-start';
  const allowTextWrap = styles.allowTextWrap;
  const shouldWrapText = allowTextWrap;

  if (isEmptyCell) {
    return (
      <Box
        sx={mergeSx(
          {
            flexDirection: cellDirection,
            justifyContent: 'center',
          },
          mergeSx(styles.outcomeCell, mixedMarket && styles.mixedMarkets)
        )}
      >
        <Typography
          component='span'
          sx={{
            color: 'neutral.25',
            textAlign: 'center',
          }}
        >
          -
        </Typography>
      </Box>
    );
  }

  const outcomeTypographyProps = getOutcomeTypographyProps(mixedMarket, shouldWrapText);

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!interactionDisabled) {
      e.stopPropagation();
      publishBetslipAnimationOrigin(e.currentTarget);
      onClick && onClick();
    }
  };

  return (
    <Box onClick={handleClick} sx={mergeSx(styles.outcomeCell, mixedMarket && styles.mixedMarkets)}>
      <Stack
        sx={mergeSx(
          {
            flexDirection: cellDirection,
            justifyContent: cellAlignment,
          },
          mergeSx(styles.oddsStack, mixedMarket && styles.mixedMarkets)
        )}
      >
        {hasOMSpecialValues && (
          <Typography
            component='span'
            noWrap={!shouldWrapText}
            sx={{
              color: styles.specialValueColor,
              textAlign: 'center',
              maxWidth: '100%',
            }}
          >
            <BetDetails.SpecialValues
              outcomeName={outcome.name}
              sportId={sportId}
              specialValues={specialValues}
              marketTypeName={marketTypeName}
              wrapInParenthesis={false}
              numericOnly={mixedMarket}
              noWrap={!shouldWrapText}
            />
            {mixedMarket && handleOverUnder(outcome.name)}
          </Typography>
        )}
        {shouldShowFTBSpecialValue && (
          <Typography
            component='span'
            noWrap={!shouldWrapText}
            sx={{
              color: styles.specialValueColor,
              textAlign: 'center',
              maxWidth: '100%',
            }}
          >
            {outcome.properties?.specialValue}
          </Typography>
        )}
        {outcomeName && (
          <BetDetails.Outcome
            outcomeName={outcomeName} // this can be short name - getOutcomeName()
            sx={{ color: styles.labelColor }}
            numericOnly={numericOnly}
            {...outcomeTypographyProps}
            {...outcome.properties}
            outcomeShortName={outcome.shortName} // adding manually short name so it can be used in FTB markets
            marketName={marketName}
            marketTypeName={marketTypeName}
            marketDisplayType={marketDisplayType}
            participants={participants}
            eventName={eventName}
          />
        )}
        <OutcomeTableCellOdds
          outcome={outcome}
          selected={selected}
          interactionDisabled={interactionDisabled}
          searchVariant={searchVariant}
          mixedMarket={mixedMarket}
          featuredCardVariant={featuredCardVariant}
        />
      </Stack>
    </Box>
  );
};

export default memo(OutcomeTableCell);
